import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';
import { ArrowLeft, Loader2, Users, Ticket, Search } from 'lucide-react';

interface Team {
  teamId: string;
  name: string;
  themeId: string | null;
  members: Array<{ sub: string; name: string; role: string }>;
  inviteCode: string;
}

interface EventSettings {
  minTeamSize: number;
  maxTeamSize: number;
  timerDurationSeconds: number;
}

interface EventTheme {
  id: string;
  name: string;
  description: string;
}

export function TeamJoinPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [teams, setTeams] = useState<Team[]>([]);
  const [maxTeamSize, setMaxTeamSize] = useState(5);
  const [themes, setThemes] = useState<EventTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [joiningByCode, setJoiningByCode] = useState(false);
  const [joiningTeamId, setJoiningTeamId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: Team[] }>(`/api/events/${eventId}/teams`),
      networkManager.get<{ status: boolean; data: { settings: EventSettings; themes: EventTheme[] } }>(`/api/events/${eventId}`),
    ])
      .then(([teamsRes, eventRes]) => {
        setTeams(teamsRes.data);
        setMaxTeamSize(eventRes.data.settings?.maxTeamSize ?? 5);
        setThemes(eventRes.data.themes ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [eventId]);

  async function handleJoinByCode(e: React.FormEvent) {
    e.preventDefault();
    const code = inviteCode.trim().toUpperCase();
    if (!code) {
      setCodeError('Please enter an invite code.');
      return;
    }
    if (!/^BYT-[A-HJ-NP-Z2-9]{4}$/i.test(code)) {
      setCodeError('Invalid format. Invite codes look like BYT-X7K2.');
      return;
    }

    setCodeError('');
    setJoiningByCode(true);
    try {
      const res = await networkManager.post<{ status: boolean; data: { teamId: string } }>(
        `/api/events/${eventId}/teams/join`,
        { inviteCode: code },
      );
      navigate(`/events/${eventId}/teams/${res.data.teamId}`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Invalid invite code. Please try again.';
      setCodeError(message);
    } finally {
      setJoiningByCode(false);
    }
  }

  async function handleJoinTeam(teamId: string) {
    setError('');
    setJoiningTeamId(teamId);
    try {
      await networkManager.post<{ status: boolean }>(
        `/api/events/${eventId}/teams/${teamId}/join`,
      );
      navigate(`/events/${eventId}/teams/${teamId}`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to join team. Please try again.';
      setError(message);
    } finally {
      setJoiningTeamId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  const openTeams = teams.filter((t) => t.members.length < maxTeamSize);

  return (
    <div className="animate-fade-in">
      <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Back to Event
      </Link>

      <h1 className="font-display text-3xl font-bold text-ink">Join a Team</h1>
      <p className="mt-1 text-ink-secondary">Use an invite code or browse open teams below.</p>

      {error && (
        <div className="mt-4 card border-red-300 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Invite Code Section */}
      <section className="mt-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Ticket className="h-5 w-5" /> Join with Invite Code
        </h2>
        <form onSubmit={handleJoinByCode} className="mt-3 flex max-w-md items-start gap-3">
          <div className="flex-1">
            <input
              type="text"
              className={`input-base w-full ${codeError ? 'border-red-400' : ''}`}
              placeholder="e.g. BYT-X7K2"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              disabled={joiningByCode}
            />
            {codeError && <p className="mt-1 text-sm text-red-600">{codeError}</p>}
          </div>
          <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={joiningByCode}>
            {joiningByCode ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Join
          </button>
        </form>
      </section>

      {/* Browse Open Teams */}
      <section className="mt-8">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Search className="h-5 w-5" /> Browse Open Teams
        </h2>

        {openTeams.length === 0 ? (
          <p className="mt-3 text-ink-secondary">No open teams available at the moment.</p>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {openTeams.map((team) => (
              <div key={team.teamId} className="card p-4 flex flex-col justify-between">
                <div>
                  <h3 className="font-medium text-ink">{team.name}</h3>
                  <span className="badge mt-1 bg-blue-100 text-blue-700">
                    {(team.themeId && themes.find((t) => t.id === team.themeId)?.name) || 'No theme'}
                  </span>
                  <div className="mt-2 flex items-center gap-1 text-sm text-ink-secondary">
                    <Users className="h-4 w-4" />
                    {team.members.length}/{maxTeamSize} members
                  </div>
                </div>
                <button
                  className="btn-secondary mt-3 w-full inline-flex items-center justify-center gap-2"
                  onClick={() => handleJoinTeam(team.teamId)}
                  disabled={joiningTeamId === team.teamId}
                >
                  {joiningTeamId === team.teamId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Join Team
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
